import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography
} from '@mui/material'
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Editor, getSnapshot, TLStoreSnapshot } from 'tldraw'
import 'tldraw/tldraw.css'

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'

// Lazy load the Tldraw component
const Tldraw = lazy(() =>
  import('tldraw').then(module => ({ default: module.Tldraw }))
)

export interface OfficeDrawingEditorProps {
  initialData?: string | null
  onDataChange: (data: string) => void
  onClear: () => void
  onEditorMount?: (editor: Editor) => void
  disabled?: boolean
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

const DrawingEditorSkeleton: React.FC = () => (
  <Box sx={{ width: '100%', minHeight: '400px' }}>
    <Skeleton variant='rectangular' width='100%' height={400} />
  </Box>
)

const OfficeDrawingEditorContent: React.FC<OfficeDrawingEditorProps> = ({
  initialData,
  onDataChange,
  onClear,
  onEditorMount,
  disabled = false
}) => {
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const editorRef = useRef<Editor | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Parse initial data
  const initialSnapshot = useMemo(() => {
    if (!initialData) return undefined
    try {
      return JSON.parse(initialData) as TLStoreSnapshot
    } catch (error) {
      console.error('Failed to parse initial drawing data:', error)
      return undefined
    }
  }, [initialData])

  // Debounced onChange handler
  const debouncedOnChange = useMemo(
    () =>
      debounce((editor: Editor) => {
        try {
          const snapshot = getSnapshot(editor.store)
          const serialized = JSON.stringify(snapshot)
          onDataChange(serialized)
        } catch (error) {
          console.error('Failed to serialize drawing data:', error)
        }
      }, 2000),
    [onDataChange]
  )

  // Handle editor mount
  const handleMount = useCallback(
    (editor: Editor) => {
      editorRef.current = editor
      // Call parent's onEditorMount if provided
      if (onEditorMount) {
        onEditorMount(editor)
      }
    },
    [onEditorMount]
  )

  // Handle store changes
  useEffect(() => {
    if (!editorRef.current) return undefined

    const editor = editorRef.current
    const handleChange = () => {
      if (!disabled) {
        debouncedOnChange(editor)
      }
    }

    // Listen to store changes - try without scope parameter for v2.x compatibility
    const unsubscribe = editor.store.listen(handleChange)

    return () => {
      unsubscribe()
    }
  }, [debouncedOnChange, disabled])

  // Handle clear drawing
  const handleClearClick = () => {
    setShowClearDialog(true)
  }

  const handleClearConfirm = () => {
    if (editorRef.current) {
      editorRef.current.selectAll()
      editorRef.current.deleteShapes(editorRef.current.getSelectedShapeIds())
      editorRef.current.selectNone()
    }
    onClear()
    setShowClearDialog(false)
  }

  const handleClearCancel = () => {
    setShowClearDialog(false)
  }

  // Handle fullscreen toggle
  const handleFullscreenToggle = () => {
    if (!containerRef.current) return

    if (!isFullscreen) {
      // Enter fullscreen
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen()
      }
    }
  }

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  return (
    <>
      <Card className='office-drawing-editor' ref={containerRef}>
        <CardContent>
          {!isFullscreen && (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography variant='h6' component='h2' gutterBottom>
                  Office Layout Drawing
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Create a visual representation of your office layout using
                  drawing tools
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            role='application'
            aria-label='Office layout drawing editor'
            aria-describedby='drawing-editor-description'
            sx={{
              position: 'relative',
              minHeight: isFullscreen ? '100vh' : '400px',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '& .tlui-debug-panel': { display: 'none' },
              '& .tlui-help-menu': { display: 'none' },
              '& .tlui-navigation-zone': { display: 'none' },
              '& .tlui-style-panel__wrapper': { display: 'none' }
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1000,
                display: 'flex',
                gap: 1
              }}
            >
              <IconButton
                onClick={handleClearClick}
                disabled={disabled}
                aria-label='Clear office layout drawing'
                color='error'
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'background.paper' }
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
              <IconButton
                onClick={handleFullscreenToggle}
                disabled={disabled}
                aria-label={
                  isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
                }
                color='primary'
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'background.paper' }
                }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Box>
            <Typography
              id='drawing-editor-description'
              sx={{ position: 'absolute', left: '-10000px' }}
            >
              Use this editor to create a visual layout of your office. You can
              add shapes, lines, and text to represent desks and areas.
            </Typography>
            <div
              style={{
                height: isFullscreen ? '100vh' : '400px',
                width: '100%'
              }}
            >
              <Tldraw
                onMount={handleMount}
                autoFocus={false}
                snapshot={initialSnapshot}
                components={{
                  PageMenu: null,
                  MainMenu: null,
                  QuickActions: null,
                  ActionsMenu: null,
                  HelpMenu: null,
                  NavigationPanel: null,
                  SharePanel: null
                }}
              />
            </div>
          </Box>
        </CardContent>
      </Card>

      {/* Clear Confirmation Dialog - outside fullscreen container */}
      <Dialog
        open={showClearDialog}
        onClose={handleClearCancel}
        aria-labelledby='clear-dialog-title'
        aria-describedby='clear-dialog-description'
        sx={{ zIndex: 10000 }}
      >
        <DialogTitle id='clear-dialog-title'>Clear Drawing?</DialogTitle>
        <DialogContent>
          <DialogContentText id='clear-dialog-description'>
            Are you sure you want to clear the entire office layout drawing?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearCancel} autoFocus>
            Cancel
          </Button>
          <Button onClick={handleClearConfirm} color='error'>
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const OfficeDrawingEditor: React.FC<OfficeDrawingEditorProps> = props => {
  const [error, setError] = useState<Error | null>(null)

  const handleError = (error: Error) => {
    console.error('Drawing editor error:', error)
    setError(error)
  }

  if (error) {
    return (
      <Card className='office-drawing-editor'>
        <CardContent>
          <Alert severity='error'>
            Failed to load drawing editor. Please refresh the page and try
            again.
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <ErrorBoundary
      onError={handleError}
      errorComponent={
        <Card className='office-drawing-editor'>
          <CardContent>
            <Alert severity='error'>
              Failed to load drawing editor. Please refresh the page and try
              again.
            </Alert>
          </CardContent>
        </Card>
      }
    >
      <Suspense fallback={<DrawingEditorSkeleton />}>
        <OfficeDrawingEditorContent {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

export default OfficeDrawingEditor
